// client/src/utils/cameraAccess.js

class CameraManager {
    constructor() {
        this.stream = null;
        this.videoElement = null;
        this.isInitialized = false;
    }

    /**
     * Initialize the camera with the specified video element
     * @param {HTMLVideoElement} videoElement - The video element to use
     * @returns {Promise<boolean>} - Whether initialization was successful
     */
    async initialize(videoElement) {
        if (!videoElement) {
            console.error('No video element provided');
            return false;
        }

        this.videoElement = videoElement;

        try {
            // Check if we're in a secure context (needed for camera access)
            if (!window.isSecureContext) {
                throw new Error('Camera access requires a secure context (HTTPS)');
            }

            // Request camera access
            const constraints = {
                video: {
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                    facingMode: 'user' // Front camera
                },
                audio: false
            };

            this.stream = await navigator.mediaDevices.getUserMedia(constraints);

            // Connect stream to video element
            this.videoElement.srcObject = this.stream;

            // Wait for video to be ready
            await new Promise((resolve) => {
                this.videoElement.onloadedmetadata = () => {
                    this.videoElement.play();
                    resolve();
                };
            });

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.error('Failed to access camera:', error);
            return false;
        }
    }

    /**
     * Stop the camera stream and clean up resources
     */
    stopCamera() {
        if (this.stream) {
            // Stop all tracks in the stream
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        if (this.videoElement) {
            this.videoElement.srcObject = null;
        }

        this.isInitialized = false;
    }

    /**
     * Get the current camera frame as an ImageData object
     * @returns {ImageData|null} - Current camera frame data or null if not available
     */
    getCurrentFrame() {
        if (!this.isInitialized || !this.videoElement) {
            return null;
        }

        const canvas = document.createElement('canvas');
        canvas.width = this.videoElement.videoWidth;
        canvas.height = this.videoElement.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(this.videoElement, 0, 0);

        return ctx.getImageData(0, 0, canvas.width, canvas.height);
    }
}

export default CameraManager;